import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { Col, Form, Row, Spinner } from 'react-bootstrap';

function FieldStatus({ status }) {
  if (status === 'saving') return <Spinner size="sm" animation="border" className="ms-2" />;
  if (status === 'saved') return <span className="ms-2 text-success small">✓ Saved</span>;
  if (status === 'error') return <span className="ms-2 text-danger small">✕ Failed to save</span>;
  return null;
}

function ProfileField({ label, initialValue, fieldKey, onSave, type, hint, disabled }) {
  const [val, setVal] = useState(initialValue || '');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setVal(initialValue || '');
  }, [initialValue]);

  async function handleBlur() {
    if (disabled || val === (initialValue || '')) return;
    setStatus('saving');
    try {
      await onSave(fieldKey, val);
      setStatus('saved');
      setTimeout(() => setStatus(null), 2500);
    } catch {
      setStatus('error');
    }
  }

  return (
    <Form.Group className="mb-4">
      <Form.Label className="settings-field-label">{label}</Form.Label>
      <div className="d-flex align-items-center">
        <Form.Control
          type={type || 'text'}
          value={val}
          onChange={e => !disabled && setVal(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          style={{ maxWidth: '360px' }}
        />
        {!disabled && <FieldStatus status={status} />}
      </div>
      {hint && <Form.Text className="text-muted">{hint}</Form.Text>}
    </Form.Group>
  );
}

function VerifyBlock({ label, pendingValue, onVerify }) {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null);

  async function handleVerify() {
    setStatus('saving');
    try {
      await onVerify(code);
      setStatus(null);
      setCode('');
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="mb-4 p-3 bg-light rounded" style={{ maxWidth: '360px' }}>
      <p className="mb-2 small fw-semibold">
        Enter the verification code sent to <strong>{pendingValue}</strong>:
      </p>
      <div className="d-flex gap-2 align-items-center">
        <Form.Control
          size="sm"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="6-digit code"
          style={{ width: '140px' }}
        />
        <button
          className="btn btn-sm btn-primary"
          onClick={handleVerify}
          disabled={status === 'saving'}
        >
          {status === 'saving' ? 'Verifying…' : 'Verify'}
        </button>
        {status === 'error' && (
          <span className="text-danger small">Invalid code</span>
        )}
      </div>
    </div>
  );
}

export default function ProfileSection() {
  const [attrs, setAttrs] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingPhone, setPendingPhone] = useState(null);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(u => setAttrs(u.attributes || {}))
      .catch(() => {});
  }, []);

  async function handleSave(key, value) {
    const u = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(u, { [key]: value });
    if (key === 'email') {
      await Auth.verifyCurrentUserAttribute('email');
      setPendingEmail(value);
    } else if (key === 'phone_number') {
      await Auth.verifyCurrentUserAttribute('phone_number');
      setPendingPhone(value);
    } else {
      setAttrs(prev => ({ ...prev, [key]: value }));
    }
  }

  async function handleVerifyEmail(code) {
    await Auth.verifyCurrentUserAttributeSubmit('email', code);
    const u = await Auth.currentAuthenticatedUser({ bypassCache: true });
    setAttrs(u.attributes || {});
    setPendingEmail(null);
  }

  async function handleVerifyPhone(code) {
    await Auth.verifyCurrentUserAttributeSubmit('phone_number', code);
    const u = await Auth.currentAuthenticatedUser({ bypassCache: true });
    setAttrs(u.attributes || {});
    setPendingPhone(null);
  }

  if (!attrs) {
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1">Profile</h4>
      <p className="text-muted mb-4">Update your personal information.</p>

      <Row>
        <Col xs={12} sm={6}>
          <ProfileField
            label="First Name"
            fieldKey="given_name"
            initialValue={attrs.given_name}
            onSave={handleSave}
            disabled
            hint="Name cannot be changed after sign-up."
          />
        </Col>
        <Col xs={12} sm={6}>
          <ProfileField
            label="Last Name"
            fieldKey="family_name"
            initialValue={attrs.family_name}
            onSave={handleSave}
            disabled
            hint="Name cannot be changed after sign-up."
          />
        </Col>
      </Row>

      <ProfileField
        label="Email"
        fieldKey="email"
        initialValue={attrs.email}
        type="email"
        onSave={handleSave}
        hint="A verification code will be sent to confirm your new email address."
      />
      {pendingEmail && (
        <VerifyBlock
          label="email"
          pendingValue={pendingEmail}
          onVerify={handleVerifyEmail}
        />
      )}

      <ProfileField
        label="Phone Number"
        fieldKey="phone_number"
        initialValue={attrs.phone_number}
        type="tel"
        onSave={handleSave}
        hint="Include country code, e.g. +1 555 123 4567. A verification code will be sent via SMS."
      />
      {pendingPhone && (
        <VerifyBlock
          label="phone"
          pendingValue={pendingPhone}
          onVerify={handleVerifyPhone}
        />
      )}
    </div>
  );
}
